import {
  createContext,
  useState,
  useEffect,
  useReducer,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import {
  IShoppingCart,
  IShoppingCartContext,
  ICheckoutProductError,
  ShoppingCartProductType,
  IOrderItem,
  IShoppingCartProduct,
  IFirebaseShoppingCartProducts,
} from '@monofront/types';
import { userService } from '@services/businessContainer';
import { useAuthentication } from './businessContainer';
import {
  shoppingCartService,
  growthBookService,
} from '@services/businessContainer';
import {
  Channel,
  FREE_SHIPPING_THRESHOLD,
} from '@monofront/business/constants';
import { useDidUpdateEffect, useLocalStorage } from '@monofront/business/hooks';
import logger from '@utils/logger';
import router from 'next/router';
import { getIsFresh, getIsUltraFresh } from '@monofront/business/utils';
import { LOCALSTORAGE_SHOPPING_CART_ID } from '@constants/shoppingCart';
import {
  useGrowthBook,
  useProductCustomer,
} from '@monofront/business/contexts';
import { trackEvent } from '@monofront/tracking';
import { computeFreeDeliveryInfos } from '@monofront/business/services';
import getConfig from 'next/config';
import { CartUpdateStatus } from '@monofront/business/services/shoppingCart/cartUpdates';

export enum checkAddressSource {
  FRESH_ADD_TO_CART = 'FRESH_ADD_TO_CART',
  ULTRA_FRESH_ADD_TO_CART = 'ULTRA_FRESH_ADD_TO_CART',
  DEFAULT = 'DEFAULT',
}

interface ISideCart {
  isOpen: boolean;
  shouldAddressBeVerified: boolean;
  openFromSideCart?: boolean;
  addFreshProductToCart?: () => void;
  addFreshProductsToCart?: () => void;
  source?: checkAddressSource;
}

type SideCartAction =
  | {
      type: 'checkAddress';
      addFreshProductToCart?: () => void;
      addFreshProductsToCart?: () => void;
      openFromSideCart?: boolean;
      source?: checkAddressSource;
    }
  | { type: 'viewMyCart' }
  | { type: 'close' };

interface IShoppingCartContextWeb extends IShoppingCartContext {
  shoppingCart: IShoppingCart;
  pfand?: number;
  addProductsToShoppingCart: (
    shoppingCartId: string,
    shoppingCartProducts: ShoppingCartProductType[] | IOrderItem[],
  ) => Promise<void>;
  deleteCurrentShoppingCart: () => Promise<void>;
  addProductsToShoppingCartBySku: (
    shoppingCartId: string,
    shoppingCartProducts: ShoppingCartProductType[] | IOrderItem[],
    channel: Channel,
  ) => Promise<void>;
  checkoutProductErrors: ICheckoutProductError[];
  setCheckoutProductErrors: Dispatch<SetStateAction<ICheckoutProductError[]>>;
  deleteFreshAndUltraFreshProductsFromCart: (
    shoppingCartId: string,
    products?: IShoppingCartProduct[],
  ) => Promise<void>;
  refreshProducts: () => Promise<void>;
}

interface ISideCartContext {
  sideCart: ISideCart;
  dispatchSideCart: Dispatch<SideCartAction>;
}

const initialSideCart: ISideCart = {
  isOpen: false,
  shouldAddressBeVerified: false,
  addFreshProductToCart: () => {
    void 0;
  },
  addFreshProductsToCart: () => {
    void 0;
  },
  openFromSideCart: false,
};

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

const sideCartReducer = (_: ISideCart, action: SideCartAction) => {
  switch (action.type) {
    case 'checkAddress':
      return {
        isOpen: true,
        shouldAddressBeVerified: true,
        addFreshProductToCart: action.addFreshProductToCart,
        addFreshProductsToCart: action.addFreshProductsToCart,
        openFromSideCart: action.openFromSideCart,
        source: action.source ?? checkAddressSource.DEFAULT,
      };
    case 'viewMyCart':
      return {
        ...initialSideCart,
        isOpen: true,
        shouldAddressBeVerified: false,
      };
    case 'close':
      return initialSideCart;
    default:
      return initialSideCart;
  }
};

const SideCartContext = createContext<ISideCartContext>({
  sideCart: initialSideCart,
  dispatchSideCart: () => undefined,
});

export const ShoppingCartErrorSetterContext = createContext<Dispatch<
  SetStateAction<Array<ICheckoutProductError>>
> | null>(null);

const ShoppingCartContext = createContext<IShoppingCartContextWeb>({
  shoppingCart: {} as IShoppingCart,
  shoppingCartId: '',
  addProductToShoppingCart: () => new Promise(() => Promise.resolve()),
  addProductsToShoppingCart: () => Promise.resolve(),
  updateProductInShoppingCart: () => new Promise(() => Promise.resolve()),
  deleteProductsFromShoppingCart: () => new Promise(() => Promise.resolve()),
  decrementProductInShoppingCart: () => new Promise(() => Promise.resolve()),
  deleteFreshAndUltraFreshProductsFromCart: () =>
    new Promise(() => Promise.resolve()),
  deleteCurrentShoppingCart: () => Promise.resolve(),
  addProductsToShoppingCartBySku: () => Promise.resolve(),
  checkoutProductErrors: [],
  setCheckoutProductErrors: () => undefined,
  refreshProducts: () => Promise.resolve(),
});

const ShoppingCartProvider = ({ children, channel }) => {
  const { customer, isAuthenticated } = useAuthentication();
  const { isMalin } = useProductCustomer();
  const growthbook = useGrowthBook();
  const [shoppingCart, setShoppingCart] = useState<IShoppingCart>(
    {} as IShoppingCart,
  );

  const [checkoutProductErrors, setCheckoutProductErrors] = useState<
    ICheckoutProductError[]
  >([]);

  const [localStorageShoppingCartID, setLocalStorageShoppingCartID] =
    useLocalStorage<string>(LOCALSTORAGE_SHOPPING_CART_ID, '');

  const [shoppingCartId, setShoppingCartId] = useState<string>(
    localStorageShoppingCartID,
  );

  const [isShoppingCartDeleted, setIsShoppingCartDeleted] = useState(false);
  const [sideCart, dispatchSideCart] = useReducer(
    sideCartReducer,
    initialSideCart,
  );

  const createNewShoppingCart = async () => {
    const createdShoppingCartId: string =
      await shoppingCartService.createShoppingCart();

    setShoppingCartId(createdShoppingCartId);
    setLocalStorageShoppingCartID(createdShoppingCartId);
  };

  useEffect(() => {
    const closeSideCart = (_: any, { shallow }: { shallow: boolean }) => {
      /** avoid to close the side cart on redirections for tracking (collections and search pages) */
      if (shallow) {
        return;
      }

      dispatchSideCart({ type: 'close' });
    };

    router.events.on('routeChangeStart', closeSideCart);

    return () => {
      router.events.off('routeChangeStart', closeSideCart);
    };
  }, []);

  useEffect(() => {
    if (isShoppingCartDeleted) {
      createNewShoppingCart();
      setIsShoppingCartDeleted(false);
    }
  }, [isShoppingCartDeleted]);

  useEffect(() => {
    const pushLocalShoppingCartIdToCustomer = async (
      shoppingCartId: string,
      customerUid: string,
    ) => {
      await userService.updateShoppingCartId(shoppingCartId, customerUid);
    };

    const mergeLocalAndCustomerShoppingCarts = async (
      shoppingCartId: string | null,
      customerShoppingCartId: string,
    ) => {
      if (shoppingCartId) {
        await shoppingCartService.mergeLocalAndCustomerShoppingCartsInDb(
          shoppingCartId,
          customerShoppingCartId,
        );
      }

      setShoppingCartId(customerShoppingCartId);
      setLocalStorageShoppingCartID(customerShoppingCartId);
    };

    const checkAndMergeShoppingCarts = async () => {
      if (!customer?.shoppingCartId) {
        return;
      }

      const customerShoppingCartIdExistsInFirebase =
        await shoppingCartService.shoppingCartIdExistsInDb(
          customer.shoppingCartId,
        );

      if (customerShoppingCartIdExistsInFirebase) {
        mergeLocalAndCustomerShoppingCarts(
          shoppingCartId,
          customer.shoppingCartId,
        );

        setShoppingCartId(customer.shoppingCartId);
        setLocalStorageShoppingCartID(customer.shoppingCartId);
      } else if (shoppingCartId) {
        pushLocalShoppingCartIdToCustomer(shoppingCartId, customer.uid);
      }
    };

    if (customer && !customer.shoppingCartId && shoppingCartId) {
      logger.info(
        'No remote shopping cart, pushing local shopping cart to remote',
        { localShoppingCartId: shoppingCartId },
      );

      pushLocalShoppingCartIdToCustomer(shoppingCartId, customer.uid);

      return;
    }

    if (
      customer &&
      customer.shoppingCartId &&
      shoppingCartId !== customer.shoppingCartId
    ) {
      logger.info('Remote and local shopping cart are different, merging', {
        remoteShoppingCartId: customer.shoppingCartId,
        localShoppingCartId: shoppingCartId,
      });

      checkAndMergeShoppingCarts();

      return;
    }

    if (!shoppingCartId) {
      logger.info('No local shopping cart, creating one', {
        remoteShoppingCartId: customer?.shoppingCartId,
        localShoppingCartId: shoppingCartId,
      });

      createNewShoppingCart();

      return;
    }
  }, [customer?.uid, customer?.shoppingCartId, shoppingCartId]);

  useEffect(() => {
    if (!shoppingCartId) {
      return;
    }

    logger.info('Subscribing to shopping cart changes', {
      remoteShoppingCartId: customer?.shoppingCartId,
      localShoppingCartId: shoppingCartId,
    });

    const updateShoppingCartAndTrack = (
      updateStatus: CartUpdateStatus,
      shoppingCart: IShoppingCart,
    ) => {
      const cartStatus = [] as Array<string>;

      if (shoppingCart?.hasUltraFreshProduct) {
        cartStatus.push('ultra_fresh');
      }
      if (shoppingCart?.hasFreshProduct) {
        cartStatus.push('fresh');
      }

      growthBookService.setGrowthbookAttributes({
        cartStatus,
      });

      if (updateStatus !== 'stale') {
        const { totalPrice, products } = shoppingCart;

        const freeDeliveryThreshold = growthbook.getFeatureValue(
          ...FREE_SHIPPING_THRESHOLD,
        );

        const { amountUntilFreeDeliveryInCents } = computeFreeDeliveryInfos(
          totalPrice,
          freeDeliveryThreshold,
        );

        trackEvent({
          type: 'LFCartUpdated',
          data: {
            total_price: totalPrice,
            amount_until_free_delivery: amountUntilFreeDeliveryInCents,
            cart: products.map((product) => ({
              name: product.title,
              sku: product.sku,
              price: product.price * 100,
              brand: product.vendor,
              category: product.product_type,
              quantity: product.quantity,
              compareAtPrice: product.compare_at_price,
              imageUrl: product.image,
              url: `${appBaseUrl}/products/${product.handle}`,
            })),
          },
        });
      }

      setShoppingCart(shoppingCart);
    };

    const unlisten = shoppingCartService.listenToShoppingCart(
      shoppingCartId,
      channel,
      updateShoppingCartAndTrack,
      setIsShoppingCartDeleted,
      { isCustomerMalin: isMalin },
    );

    return () => {
      unlisten();
    };
  }, [shoppingCartId, isMalin]);

  // Create a new shopping cart when logging out (to prevent conflict with the previous user cart)
  useDidUpdateEffect(() => {
    if (shoppingCartId && !isAuthenticated) {
      createNewShoppingCart();
    }
  }, [isAuthenticated]);

  const addProductToShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProduct: ShoppingCartProductType,
  ) => {
    await shoppingCartService.addProductToShoppingCart(
      shoppingCartId,
      shoppingCartProduct,
    );
  };

  const addProductsToShoppingCartBySku = async (
    shoppingCartId: string,
    shoppingCartProducts: ShoppingCartProductType[] | IOrderItem[],
    channel: Channel,
  ) => {
    await shoppingCartService.addProductsToShoppingCartBySku(
      shoppingCartId,
      shoppingCartProducts,
      channel,
    );
  };

  const addProductsToShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProducts: ShoppingCartProductType[] | IOrderItem[],
  ) => {
    await shoppingCartService.addProductsToShoppingCart(
      shoppingCartId,
      shoppingCartProducts,
    );
  };

  const updateProductInShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProduct: ShoppingCartProductType,
  ) => {
    await shoppingCartService.updateProductInShoppingCart(
      shoppingCartId,
      shoppingCartProduct,
    );
  };

  const deleteProductsFromShoppingCart = async (
    shoppingCartIdArg: string | undefined = shoppingCartId,
    products?: Array<{ sku: string }>,
  ) => {
    if (!shoppingCart || !shoppingCartIdArg) {
      return;
    }

    // delete specific products given in parameter, otherwise delete all products from shopping cart
    const productsToDelete = products || shoppingCart.products;

    await shoppingCartService.deleteProductsFromShoppingCart(
      shoppingCartIdArg,
      productsToDelete as ShoppingCartProductType[], // ShoppingCartProductType is a shorter version of IShoppingCartProduct
    );
  };

  const decrementProductInShoppingCart = async (
    shoppingCartId: string,
    product: ShoppingCartProductType,
  ) => {
    await shoppingCartService.decrementProductInShoppingCart(
      shoppingCartId,
      product,
    );
  };

  const deleteFreshAndUltraFreshProductsFromCart = async (
    shoppingCartId: string,
    products?: IShoppingCartProduct[],
  ) => {
    return await deleteProductsFromShoppingCart(
      shoppingCartId,
      products?.filter(
        (product) => getIsFresh(product.tags) || getIsUltraFresh(product.tags),
      ),
    );
  };

  const deleteCurrentShoppingCart = () => {
    if (customer?.shoppingCartId) {
      shoppingCartService.delete(customer.shoppingCartId);
    }

    return Promise.resolve();
  };

  const refreshProducts = useCallback(async () => {
    if (!shoppingCart.shoppingCartId) {
      logger.error('Wanted to refresh products but shoppingCartId is missing');

      return;
    }

    const productsInFirebaseFormat = shoppingCart.products.reduce(
      (acc, product) => {
        acc[product.sku] = product.quantity;

        return acc;
      },
      {} as IFirebaseShoppingCartProducts,
    );

    await shoppingCartService.getProductsFromSearchService(
      productsInFirebaseFormat,
      shoppingCart.shoppingCartId,
      channel,
      { isCustomerMalin: isMalin },
      true,
    );
  }, [shoppingCart.products, shoppingCart.shoppingCartId, isMalin, channel]);

  return (
    <ShoppingCartContext.Provider
      value={{
        deleteFreshAndUltraFreshProductsFromCart,
        shoppingCart,
        shoppingCartId,
        addProductToShoppingCart,
        addProductsToShoppingCart,
        updateProductInShoppingCart,
        decrementProductInShoppingCart,
        deleteProductsFromShoppingCart,
        deleteCurrentShoppingCart,
        addProductsToShoppingCartBySku,
        checkoutProductErrors: checkoutProductErrors,
        setCheckoutProductErrors: setCheckoutProductErrors,
        refreshProducts,
      }}
    >
      <ShoppingCartErrorSetterContext.Provider value={setCheckoutProductErrors}>
        <SideCartContext.Provider value={{ sideCart, dispatchSideCart }}>
          {children}
        </SideCartContext.Provider>
      </ShoppingCartErrorSetterContext.Provider>
    </ShoppingCartContext.Provider>
  );
};

export { ShoppingCartProvider, ShoppingCartContext, SideCartContext };
